import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import CardDetail from '@carers/components/CardDetail/CardDetail';
import InterimAlert from '@carers/components/PaymentCard/InterimAlert';
import IsAtHomeMissionAlert from '@carers/components/PaymentCard/IsAtHomeMissionAlert';
import ShortTermAlert from '@carers/components/PaymentCard/ShortTermAlert';
import useCalculatePaymentRemuneration from '@carers/components/PaymentCard/useCalculatePaymentRemuneration';
import { Mission } from '@carers/views/Missions/models/Missions';
import { Request } from '@carers/views/Requests/models/Requests';
import PaymentsIcon from '@shared/assets/icons/payments.svg';
import { CONTRACTS } from '@shared/constants/missionsConstants';

type PaymentCardProps = {
  mission: Request | Mission;
  selectedDateSchedulesIds?: string[];
};

const isMission = (mission: Request | Mission): mission is Mission =>
  (mission as Mission).status !== undefined;

const PaymentCard = ({ mission, selectedDateSchedulesIds }: PaymentCardProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'carers' });

  const {
    cumulatedGrossRemuneration,
    cumulatedNetRemuneration,
    cumulatedRemuneration,
    cumulatedMinimumGrossRemuneration,
  } = useCalculatePaymentRemuneration(mission, selectedDateSchedulesIds);

  const isIndependentMission = mission.contract.value === CONTRACTS.INDEPENDENT;
  const isShortTermMission = mission.contract.value === CONTRACTS.SHORT_TERM;
  const isInterimMission = mission.contract.value === CONTRACTS.INTERIM;

  const getCardDetailTitle = () => {
    if (isShortTermMission && !mission.minimumGrossRemuneration) {
      return undefined;
    }

    const contractCumulatedGrossRemuneration = isShortTermMission
      ? cumulatedMinimumGrossRemuneration
      : cumulatedGrossRemuneration;

    if (selectedDateSchedulesIds && contractCumulatedGrossRemuneration === null) {
      return t('requests.detail.remunerationCurrency');
    }

    return cumulatedRemuneration.gross;
  };

  const getCardDetailTitleLight = () => {
    if (isShortTermMission) {
      if (!mission.minimumGrossRemuneration) {
        return (
          <Trans
            i18nKey={t('requests.detail.toKnowYourRemuneration')}
            components={{
              tel: (
                <a href={`tel:${mission.lead.phone}`}>{t('requests.detail.actions.contactUs')}</a>
              ),
            }}
          />
        );
      }

      return t('common.format.remuneration.seniority_recovery');
    }

    if (isInterimMission) {
      return t('common.format.remuneration.interim_compensation');
    }

    if (selectedDateSchedulesIds && cumulatedNetRemuneration === null) {
      return undefined;
    }

    return cumulatedRemuneration.net;
  };

  // eslint-disable-next-line complexity
  const getCardDetailText = () => {
    if (isShortTermMission && !mission.minimumGrossRemuneration) {
      return undefined;
    }

    if (isMission(mission)) {
      if (isIndependentMission) {
        return t('missions.detail.remunerationWarning');
      }

      return undefined;
    }

    if (!selectedDateSchedulesIds) {
      return isShortTermMission
        ? `${t('requests.detail.remunerationPurposedDate')}, ${t(
            'requests.detail.remunerationSeniorityRecognition',
          )}`
        : t('requests.detail.remunerationPurposedDate');
    }

    const remunerationSelectedDate = isShortTermMission
      ? `${t('requests.detail.remunerationSelectedDate')}, ${t(
          'requests.detail.remunerationSeniorityRecognition',
        )}`
      : t('requests.detail.remunerationSelectedDate');
    const contractCumulatedGrossRemuneration = isShortTermMission
      ? cumulatedMinimumGrossRemuneration
      : cumulatedGrossRemuneration;

    return contractCumulatedGrossRemuneration !== null
      ? remunerationSelectedDate
      : t('requests.detail.noDateSelectedText');
  };

  return (
    <div className="stack">
      <IsAtHomeMissionAlert mission={mission} />
      <ShortTermAlert mission={mission} />
      <InterimAlert mission={mission} />

      <CardDetail
        icon={PaymentsIcon}
        title={getCardDetailTitle()}
        titleLight={getCardDetailTitleLight()}
        text={getCardDetailText()}
      />
    </div>
  );
};

export default PaymentCard;
